import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { downloadFileFromBlob } from 'src/common/downloadFile';
import { SteckbriefDocumentPreview } from '../ensan-steckbrief.dto';

/** A rendered preview plus the object URL the page created (and revokes) for it. */
export type InlineSteckbriefPreview = SteckbriefDocumentPreview & { url: string };

interface SteckbriefPdfPreviewPanelProps {
    /** The last rendered preview; `null` until the first render. */
    preview: InlineSteckbriefPreview | null;
    fallbackFileName: string;
    loading: boolean;
    error: string | null;
    /** The form changed since the last calculation — the shown PDF is stale. */
    outdated: boolean;
    /** Required fields are missing — the backend would reject the render. */
    incomplete: boolean;
}

/**
 * Inline Steckbrief preview: shows the stateless `/document-preview` PDF in the
 * browser's PDF viewer, re-rendered by the form after every calculation. It is
 * the exact Flying Saucer output the generated document will have — no
 * client-side template rendering.
 */
export function SteckbriefPdfPreviewPanel({
    preview,
    fallbackFileName,
    loading,
    error,
    outdated,
    incomplete,
}: SteckbriefPdfPreviewPanelProps) {
    const { t } = useTranslation();
    const stale = outdated || incomplete;
    const hint = incomplete
        ? t('Complete the required fields to update the preview.')
        : outdated
          ? t('Recalculate to update the preview.')
          : null;

    return (
        <Box data-testid="steckbrief-preview" sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">{t('Steckbrief preview')}</Typography>
                {loading && <CircularProgress size={12} data-testid="steckbrief-preview-loading" />}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={() => preview && downloadFileFromBlob(preview.blob, preview.fileName ?? fallbackFileName)}
                    disabled={!preview || stale || loading}
                    data-testid="download-preview"
                >
                    {t('Download')}
                </Button>
            </Stack>
            {hint && (
                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mb: 1 }}>
                    {hint}
                </Typography>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                </Alert>
            )}
            {preview ? (
                // A4 portrait: the PDF viewer fits the one-page Steckbrief.
                <Box
                    sx={{
                        aspectRatio: '210 / 297',
                        maxHeight: '90vh',
                        width: '100%',
                        opacity: stale || loading ? 0.5 : 1,
                        transition: 'opacity 150ms',
                    }}
                >
                    <iframe
                        src={preview.url}
                        title={t('Steckbrief preview')}
                        data-testid="steckbrief-preview-frame"
                        style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
                    />
                </Box>
            ) : (
                !hint &&
                !error && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('The preview appears after the calculation — nothing is saved.')}
                    </Typography>
                )
            )}
        </Box>
    );
}
